<template>
	<div class="gantt-wrapper">
		<!-- Toolbar -->
		<v-toolbar flat class="background-2 round-card mb-4">
			<v-toolbar-title class="font-weight-medium">
				<v-icon left>mdi-chart-gantt</v-icon>
				Pianificazione Progetti
			</v-toolbar-title>
			<v-spacer></v-spacer>

			<!-- Filtri -->
			<v-select
				v-model="filters.statuses"
				:items="statusOptions"
				label="Stato"
				multiple
				dense
				outlined
				hide-details
				style="max-width: 200px;"
				class="mr-2"
			>
				<template v-slot:selection="{ item, index }">
					<span v-if="index === 0">{{ item.text }}</span>
					<span v-if="index === 1" class="grey--text text-caption ml-1">
						(+{{ filters.statuses.length - 1 }})
					</span>
				</template>
			</v-select>

			<v-autocomplete
				v-model="filters.user_ids"
				:items="users"
				item-text="text"
				item-value="value"
				label="Utenti"
				multiple
				dense
				outlined
				hide-details
				clearable
				style="max-width: 200px;"
				class="mr-2"
			>
				<template v-slot:selection="{ item, index }">
					<span v-if="index === 0">{{ item.text }}</span>
					<span v-if="index === 1" class="grey--text text-caption ml-1">
						(+{{ filters.user_ids.length - 1 }})
					</span>
				</template>
			</v-autocomplete>

			<v-menu
				v-model="dateMenu"
				:close-on-content-click="false"
				transition="scale-transition"
				offset-y
				min-width="auto"
			>
				<template v-slot:activator="{ on }">
					<v-btn outlined small v-on="on" class="mr-2">
						<v-icon left small>mdi-calendar-range</v-icon>
						{{ dateRangeLabel }}
					</v-btn>
				</template>
				<v-card min-width="320">
					<v-card-text class="pb-0">
						<gmh-date-picker
							v-model="filters.start_date"
							@change="onStartDatePicked"
							>
							<!-- il campo si digita a mano, il picker si apre solo dall'icona -->
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="startDateInput"
									label="Da"
									placeholder="gg/mm/aaaa"
									dense
									outlined
									hide-details
									class="mb-3"
									v-bind="attrs"
									v-mask="'##/##/####'"
									@blur="parseStartDate"
									@keyup.enter="parseStartDate"
								>
									<template v-slot:append>
										<v-icon v-on="on" style="cursor: pointer;">mdi-calendar</v-icon>
									</template>
								</v-text-field>
							</template>
						</gmh-date-picker>

						<gmh-date-picker
							v-model="filters.end_date"
							@change="onEndDatePicked"
							>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="endDateInput"
									label="A"
									placeholder="gg/mm/aaaa"
									dense
									outlined
									hide-details
									v-bind="attrs"
									v-mask="'##/##/####'"
									@blur="parseEndDate"
									@keyup.enter="parseEndDate"
								>
									<template v-slot:append>
										<v-icon v-on="on" style="cursor: pointer;">mdi-calendar</v-icon>
									</template>
								</v-text-field>
							</template>
						</gmh-date-picker>
					</v-card-text>
					<v-card-actions>
						<v-btn text small color="error" @click="clearDateFilter" :disabled="!hasDateFilter">
							<v-icon left small>mdi-close</v-icon>
							Rimuovi
						</v-btn>
						<v-spacer></v-spacer>
						<v-btn text small @click="dateMenu = false">Chiudi</v-btn>
						<v-btn color="primary" small @click="applyDateFilter">Applica</v-btn>
					</v-card-actions>
				</v-card>
			</v-menu>

			<v-btn color="primary" small @click="loadData" :loading="loading">
				<v-icon left small>mdi-refresh</v-icon>
				Aggiorna
			</v-btn>
		</v-toolbar>

		<!-- Loading State -->
		<div v-if="loading" class="text-center py-8">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
			<div class="text-caption grey--text mt-2">Caricamento progetti...</div>
		</div>

		<!-- Empty State -->
		<div v-else-if="!projects.length" class="text-center py-8">
			<v-icon size="64" color="grey lighten-1">mdi-chart-gantt</v-icon>
			<div class="text-h6 grey--text mt-4">Nessun progetto trovato</div>
			<div class="text-caption grey--text">Modifica i filtri per visualizzare i progetti</div>
		</div>

		<!-- Gantt Chart -->
		<v-card v-else class="elevation-2 round-card">
			<v-card-text class="pa-0">
				<!-- Chart Statistics -->
				<div class="d-flex align-center px-4 py-2 background-2">
					<v-chip small class="mr-2" color="primary" outlined>
						<v-icon left small>mdi-folder</v-icon>
						{{ chartInfo.total_projects }} progetti
					</v-chip>
					<v-chip small color="secondary" outlined>
						<v-icon left small>mdi-checkbox-marked-outline</v-icon>
						{{ chartInfo.total_tasks }} task
					</v-chip>
					<v-spacer></v-spacer>
					<div class="text-caption grey--text">
						{{ chartInfo.min_date | formatDate }} - {{ chartInfo.max_date | formatDate }}
					</div>
				</div>

				<v-divider></v-divider>

				<!-- amCharts Container -->
				<div ref="chartdiv" class="gantt-chart"></div>

				<!-- Legend -->
				<div class="d-flex align-center justify-center px-4 py-2 background-2">
					<div class="d-flex align-center mr-4">
						<div class="legend-color" style="background: #F44336;"></div>
						<span class="text-caption ml-1">Alta</span>
					</div>
					<div class="d-flex align-center mr-4">
						<div class="legend-color" style="background: #FF9800;"></div>
						<span class="text-caption ml-1">Media-Alta</span>
					</div>
					<div class="d-flex align-center mr-4">
						<div class="legend-color" style="background: #4CAF50;"></div>
						<span class="text-caption ml-1">Media</span>
					</div>
					<div class="d-flex align-center">
						<div class="legend-color" style="background: #2196F3;"></div>
						<span class="text-caption ml-1">Bassa</span>
					</div>
				</div>
			</v-card-text>
		</v-card>
	</div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'
import am4themes_material from '@amcharts/amcharts4/themes/material'
import moment from 'moment'

am4core.useTheme(am4themes_material)
am4core.useTheme(am4themes_animated)

export default {
	name: 'GanttView',

	data() {
		return {
			loading: false,
			projects: [],
			chartInfo: {
				min_date: null,
				max_date: null,
				total_projects: 0,
				total_tasks: 0
			},
			chart: null,
			dateMenu: false,
			startDateInput: '',
			endDateInput: '',
			filters: {
				statuses: ['active'],
				start_date: null,
				end_date: null,
				user_ids: []
			},
			// stati progetto (general_types project_status), arrivano con la risposta del gantt
			statusOptions: [],
			users: []
		}
	},

	computed: {
		dateRangeLabel() {
			if (this.filters.start_date && this.filters.end_date) {
				return `${moment(this.filters.start_date).format('DD/MM')} - ${moment(this.filters.end_date).format('DD/MM')}`
			} else if (this.filters.start_date) {
				return `Dal ${moment(this.filters.start_date).format('DD/MM')}`
			} else if (this.filters.end_date) {
				return `Fino al ${moment(this.filters.end_date).format('DD/MM')}`
			}
			return 'Tutte le date'
		},
		hasDateFilter() {
			return this.filters.start_date || this.filters.end_date
		}
	},

	filters: {
		formatDate(value) {
			if (!value) return ''
			return moment(value).format('DD/MM/YYYY')
		}
	},

	mounted() {
		this.loadUsers()
		this.loadData()
	},

	beforeDestroy() {
		if (this.chart) {
			this.chart.dispose()
		}
	},

	methods: {
		async loadUsers() {
			try {
				const response = await this.axios.get('/async-users')
				if (response.data) {
					this.users = response.data
				}
			} catch (error) {
				console.error('Error loading users:', error)
			}
		},

		async loadData() {
			this.loading = true
			try {
				const response = await this.axios.post('projects/gantt', {
					statuses: this.filters.statuses,
					start_date: this.filters.start_date,
					end_date: this.filters.end_date,
					user_ids: this.filters.user_ids
				})

				if (response.data) {
					this.projects = response.data.projects || []
					this.chartInfo = response.data.chart_info || {}
					this.statusOptions = response.data.statuses || []

					this.$nextTick(() => {
						this.createChart()
					})
				}
			} catch (error) {
				this.$eventBusUtility.$emit('errorHandler', error)
			} finally {
				this.loading = false
				this.$store.commit('endProgress')
			}
		},

		applyDateFilter() {
			this.dateMenu = false
			this.loadData()
		},

		clearDateFilter() {
			this.filters.start_date = null
			this.filters.end_date = null
			this.startDateInput = ''
			this.endDateInput = ''
			this.dateMenu = false
			this.loadData()
		},

		parseStartDate() {
			if (!this.startDateInput) {
				this.filters.start_date = null
				return
			}
			const parsed = moment(this.startDateInput, ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY'], true)
			if (parsed.isValid()) {
				this.filters.start_date = parsed.format('YYYY-MM-DD')
				this.startDateInput = parsed.format('DD/MM/YYYY')
			}
		},

		parseEndDate() {
			if (!this.endDateInput) {
				this.filters.end_date = null
				return
			}
			const parsed = moment(this.endDateInput, ['DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY', 'D-M-YYYY'], true)
			if (parsed.isValid()) {
				this.filters.end_date = parsed.format('YYYY-MM-DD')
				this.endDateInput = parsed.format('DD/MM/YYYY')
			}
		},

		// il menu lo chiude gmh-date-picker, qui si risincronizza solo il testo del campo
		onStartDatePicked() {
			this.startDateInput = this.filters.start_date ? moment(this.filters.start_date).format('DD/MM/YYYY') : ''
		},

		onEndDatePicked() {
			this.endDateInput = this.filters.end_date ? moment(this.filters.end_date).format('DD/MM/YYYY') : ''
		},

		createChart() {
			// Dispose existing chart
			if (this.chart) {
				this.chart.dispose()
			}

			// Ensure container is ready
			if (!this.$refs.chartdiv) {
				console.warn('Chart container not ready, retrying...')
				setTimeout(() => this.createChart(), 100)
				return
			}

			// Create chart
			let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)
			chart.hiddenState.properties.opacity = 0

			// Set Italian locale for dates
			chart.language.locale = {
				_decimalSeparator: ',',
				_thousandSeparator: '.',
				_date_day: 'dd/MM/yyyy',
				_date_month: 'MMM yyyy',
				_date_year: 'yyyy',
				'Jan': 'Gen',
				'Feb': 'Feb',
				'Mar': 'Mar',
				'Apr': 'Apr',
				'May': 'Mag',
				'Jun': 'Giu',
				'Jul': 'Lug',
				'Aug': 'Ago',
				'Sep': 'Set',
				'Oct': 'Ott',
				'Nov': 'Nov',
				'Dec': 'Dic',
				'January': 'Gennaio',
				'February': 'Febbraio',
				'March': 'Marzo',
				'April': 'Aprile',
				'May (month)': 'Maggio',
				'June': 'Giugno',
				'July': 'Luglio',
				'August': 'Agosto',
				'September': 'Settembre',
				'October': 'Ottobre',
				'November': 'Novembre',
				'December': 'Dicembre'
			}

			// Responsive settings
			chart.responsive.enabled = true

			// Set date format
			chart.dateFormatter.inputDateFormat = 'yyyy-MM-dd'

			// Create data array for Gantt
			let data = []
			let colorSet = new am4core.ColorSet()

			this.projects.forEach((project, projectIndex) => {
				// Add project row
				let projectColor = am4core.color(project.color || colorSet.getIndex(projectIndex))

				if (project.start_date && project.end_date) {
					data.push({
						name: project.name,
						category: project.name,
						fromDate: project.start_date,
						toDate: project.end_date,
						color: projectColor,
						isProject: true,
						projectId: project.id
					})
				}

				// Add tasks
				if (project.tasks && project.tasks.length > 0) {
					project.tasks.forEach(task => {
						data.push({
							name: task.name,
							category: project.name,
							fromDate: task.start_date,
							toDate: task.end_date,
							color: am4core.color(task.color || '#9E9E9E'),
							isProject: false,
							taskId: task.task_id,
							tasklistName: task.tasklist_name,
							assignees: task.assignees ? task.assignees.join(', ') : '',
							priority: task.priority
						})
					})
				}
			})

			chart.data = data

			// Create a map of project names to IDs for click handling
			let projectMap = {}
			this.projects.forEach(project => {
				projectMap[project.name] = project.project_id
			})

			// Create axes
			let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
			categoryAxis.dataFields.category = 'category'
			categoryAxis.renderer.grid.template.location = 0
			categoryAxis.renderer.inversed = true
			categoryAxis.renderer.minGridDistance = 20
			categoryAxis.renderer.labels.template.fontSize = 12
			categoryAxis.renderer.labels.template.truncate = true
			categoryAxis.renderer.labels.template.maxWidth = 200
			categoryAxis.renderer.labels.template.fill = am4core.color('#1976D2')
			categoryAxis.renderer.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer

			// Make labels clickable (left-click navigates, middle-click/ctrl+click opens new tab)
			categoryAxis.renderer.labels.template.events.on('hit', (ev) => {
				const category = ev.target.dataItem.category
				const projectId = projectMap[category]
				if (projectId) {
					const nativeEvent = ev.event
					// Ctrl+click or Meta+click opens in new tab
					if (nativeEvent && (nativeEvent.ctrlKey || nativeEvent.metaKey)) {
						window.open(`#/projects/${projectId}/`, '_blank')
					} else {
						this.openProject(projectId)
					}
				}
			})

			// Handle middle-click using native mouseup event on the SVG element
			this.$nextTick(() => {
				const svgElement = this.$refs.chartdiv?.querySelector('svg')
				if (svgElement) {
					svgElement.addEventListener('mouseup', (e) => {
						// Middle click (button 1)
						if (e.button === 1) {
							// Find the text element under cursor
							const target = e.target
							if (target.tagName === 'tspan' || target.tagName === 'text') {
								const textContent = target.textContent
								const projectId = projectMap[textContent]
								if (projectId) {
									e.preventDefault()
									window.open(`#/projects/${projectId}/`, '_blank')
								}
							}
						}
					})
				}
			})

			let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
			dateAxis.dateFormatter.dateFormat = 'dd/MM/yyyy'
			dateAxis.renderer.minGridDistance = 70
			dateAxis.baseInterval = { count: 1, timeUnit: 'day' }
			dateAxis.max = new Date(this.chartInfo.max_date).getTime()
			dateAxis.min = new Date(this.chartInfo.min_date).getTime()
			dateAxis.strictMinMax = true
			dateAxis.renderer.tooltipLocation = 0

			// Create series
			let series = chart.series.push(new am4charts.ColumnSeries())
			series.dataFields.openDateX = 'fromDate'
			series.dataFields.dateX = 'toDate'
			series.dataFields.categoryY = 'category'
			series.columns.template.propertyFields.fill = 'color'
			series.columns.template.propertyFields.stroke = 'color'
			series.columns.template.strokeOpacity = 1
			series.columns.template.height = am4core.percent(70)
			series.columns.template.tooltipText = '{name}: {openDateX.formatDate("dd/MM/yyyy")} - {dateX.formatDate("dd/MM/yyyy")}'

			// Different appearance for projects vs tasks
			series.columns.template.adapter.add('height', (height, target) => {
				if (target.dataItem && target.dataItem.dataContext.isProject) {
					return am4core.percent(90)
				}
				return am4core.percent(50)
			})

			series.columns.template.adapter.add('opacity', (opacity, target) => {
				if (target.dataItem && target.dataItem.dataContext.isProject) {
					return 0.8
				}
				return 1
			})

			// Make bars clickable
			series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer
			series.columns.template.events.on('hit', (ev) => {
				const data = ev.target.dataItem.dataContext
				if (data.isProject && data.projectId) {
					// Click on project bar - navigate to project
					const nativeEvent = ev.event
					if (nativeEvent && (nativeEvent.ctrlKey || nativeEvent.metaKey)) {
						window.open(`#/projects/${projectMap[data.category]}/`, '_blank')
					} else {
						this.openProject(projectMap[data.category])
					}
				} else if (data.taskId) {
					// Click on task bar - navigate to task page
					this.openTask(data.taskId)
				}
			})

			// Add cursor
			chart.cursor = new am4charts.XYCursor()
			chart.cursor.behavior = 'zoomX'

			// Add scrollbar
			chart.scrollbarX = new am4core.Scrollbar()

			// Store chart reference
			this.chart = chart
		},

		openProject(projectId) {
			this.$router.push(`/projects/${projectId}/`)
		},

		openTask(taskId) {
			this.$router.push(`/tasks/${taskId}`)
		}
	}
}
</script>

<style scoped>
.gantt-wrapper {
	padding: 16px;
}

.gantt-chart {
	width: 100%;
	height: 600px;
	min-height: 400px;
}

.legend-color {
	width: 16px;
	height: 16px;
	border-radius: 4px;
}
</style>
